import React from 'react';
import { Checkbox, TextInput, Toggle as SetlistToggle } from '@opendate-app/setlist';

import { REVENUE_BASE_OPTIONS, RevenueBaseTooltip } from './revenueBase';
import { hasPayoutCap } from '../utils/payoutCap';

// Section/item config for the new Deal-terms IA.
//
// Each item declares: title, description, icons, `isConfigured(ctx)`,
// `summaryChips(ctx)`, `clear(ctx)`, and `renderBody(ctx)`. The `ctx`
// passed by V3DealTermsSection bundles every dependency an item might need:
// `{ offer, budget, walkoutPotential, onChangeField, bonusManagerProps,
//    team, csrfToken, deposits }`. Items pull what they care about.
//
// Sections are rendered top-down on the "All" tab and one-at-a-time on a
// specific tab. Item order matches the screenshots.

// ── Helpers ──────────────────────────────────────────────────────────────
const fmtUSD = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '$0';
  return `$${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const fmtPct = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0%';
  return `${v}%`;
};

const SOLD_BY_LABEL = {
  artist: 'Artist',
  venue: 'Venue',
  '3rd_party': '3rd-party',
};

const formatSoldBy = (v) => SOLD_BY_LABEL[v] || (v ? String(v) : '');

// ── Field-level edit primitives ───────────────────────────────────────────
const Field = ({ label, children }) => (
  <label className="deal-terms-field">
    <span className="deal-terms-field__label">{label}</span>
    {children}
  </label>
);

const NumberInput = ({ value, onChange, step = '0.01', prefix, suffix }) => (
  <span className="deal-terms-field__input-wrap">
    {prefix && <span className="deal-terms-field__prefix">{prefix}</span>}
    <input
      type="number"
      step={step}
      className="deal-terms-field__input"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
    />
    {suffix && <span className="deal-terms-field__suffix">{suffix}</span>}
  </span>
);

const SegmentedControl = ({ value, onChange, options }) => (
  <span className="deal-terms-segmented" role="radiogroup">
    {options.map((opt) => (
      <button
        type="button"
        key={opt.value}
        className={`deal-terms-segmented__btn ${value === opt.value ? 'deal-terms-segmented__btn--active' : ''}`}
        onClick={() => onChange(opt.value)}
        aria-pressed={value === opt.value}
      >
        {opt.label}
      </button>
    ))}
  </span>
);

const Toggle = ({ checked, onChange, label }) => (
  <label className="deal-terms-toggle">
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span>{label}</span>
  </label>
);

// ── Sections ──────────────────────────────────────────────────────────────
export const SECTIONS = [
  {
    key: 'revenue_base',
    title: 'Revenue',
    icon: 'fal fa-percent',
    items: [
      {
        key: 'calculation_method',
        title: 'Calculation method',
        description: 'How splits and percentages are calculated.',
        icon: 'fal fa-sliders-h',
        isConfigured: ({ offer }) => offer.revenue_base && offer.revenue_base !== 'default',
        summaryChips: ({ offer }) => {
          const opt = REVENUE_BASE_OPTIONS.find((o) => o.value === offer.revenue_base);
          if (!opt || opt.value === 'default') return [];
          // The screenshot's chip shows "Basis NBOR" — NBOR is the abbreviation
          // for Net Box Office Receipts (i.e. net_gross). Show the short code
          // when one exists, otherwise the full label.
          const SHORT = { net_gross: 'NBOR', gross: 'Gross', adjusted_gross: 'Adj. Gross' };
          return [{ label: 'Basis', value: SHORT[opt.value] || opt.label }];
        },
        clear: ({ onChangeField }) => onChangeField('revenue_base', 'default'),
        renderBody: ({ offer, onChangeField }) => (
          <Field label={<>Basis<RevenueBaseTooltip /></>}>
            <select
              className="deal-terms-field__select"
              value={offer.revenue_base || 'default'}
              onChange={(e) => onChangeField('revenue_base', e.target.value)}
            >
              {REVENUE_BASE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
        ),
      },
      {
        key: 'breakeven',
        title: 'Breakeven',
        description: 'Threshold above which overage is split.',
        icon: 'fal fa-hashtag',
        isConfigured: ({ offer }) => offer.breakeven_threshold != null,
        summaryChips: ({ offer }) => (
          offer.breakeven_threshold != null
            ? [{ label: 'Threshold', value: fmtUSD(offer.breakeven_threshold) }]
            : []
        ),
        clear: ({ onChangeField }) => onChangeField('breakeven_threshold', null),
        renderBody: ({ offer, onChangeField }) => (
          <Field label="Threshold">
            <NumberInput
              prefix="$"
              value={offer.breakeven_threshold}
              onChange={(v) => onChangeField('breakeven_threshold', v)}
            />
          </Field>
        ),
      },
      {
        key: 'deal_brackets',
        title: 'Deal brackets',
        description: 'Tiered guarantee / split levels by ticket count or gross ticket sales.',
        icon: 'fal fa-layer-group',
        isConfigured: ({ dealBrackets }) => (
          (dealBrackets || []).filter((b) => !b._destroy).length > 0
        ),
        summaryChips: ({ dealBrackets }) => {
          const count = (dealBrackets || []).filter((b) => !b._destroy).length;
          return count > 0 ? [{ label: '', value: `${count} ${count === 1 ? 'bracket' : 'brackets'}` }] : [];
        },
        clear: ({ clearDealBrackets, onChangeField, poolMode }) => {
          if (clearDealBrackets) clearDealBrackets();
          if (!poolMode && onChangeField) onChangeField('from_dollar_one', true);
        },
        renderBody: ({ DealBracketsEditor, offer, onChangeField, poolMode, dealBrackets }) => {
          if (!DealBracketsEditor) return null;
          if (poolMode) return <DealBracketsEditor />;

          const fromDollarOne = !!offer.from_dollar_one;
          const hasBrackets = (dealBrackets || []).some((bracket) => !bracket._destroy);
          return (
            <>
              <div className="d-flex align-items-center mb-2">
                <Checkbox
                  data-testid="deal-brackets-from-dollar-one"
                  label="From dollar one"
                  checked={fromDollarOne}
                  onChange={(e) => onChangeField('from_dollar_one', e.target.checked)}
                />
                <span className="deal-terms-item__description ml-2">
                  {fromDollarOne ? '(uncheck to use deal brackets)' : (hasBrackets ? '(deal brackets active)' : '(no brackets yet)')}
                </span>
              </div>
              {!fromDollarOne && !hasBrackets && (
                <p className="deal-terms-item__description mb-2" data-testid="deal-brackets-empty-warning">
                  The deal terms above apply until you add a bracket.
                </p>
              )}
              <DealBracketsEditor />
            </>
          );
        },
      },
    ],
  },

  {
    key: 'deposit',
    title: 'Deposit',
    icon: 'fal fa-coins',
    items: [
      {
        key: 'deposit',
        title: 'Deposit',
        description: 'Deposit due before the event.',
        icon: 'fal fa-coins',
        isConfigured: ({ deposits }) => {
          const d = (deposits || [])[0];
          if (!d || d._destroy) return false;
          // "Configured" means at least the Amount is set. Type/Due defaults
          // are surfaced by the form when the user opens it.
          return d.value != null && Number(d.value) !== 0;
        },
        summaryChips: ({ deposits }) => {
          const d = (deposits || [])[0];
          if (!d || d._destroy) return [];
          const isPct = d.deposit_type === 'percent';
          const chips = [];
          if (d.value != null) chips.push({ label: 'Amount', value: isPct ? fmtPct(d.value) : fmtUSD(d.value) });
          if (d.due_date_type === 'relative' && d.relative_due_date != null) {
            chips.push({ label: 'Due', value: `${d.relative_due_date}d before` });
          } else if (d.due_date_type === 'specific' && d.specific_due_date) {
            chips.push({ label: 'Due', value: d.specific_due_date });
          }
          return chips;
        },
        // Clearing tombstones the existing deposit (or resets locally when
        // there's no persisted id yet).
        clear: ({ deposits, onChangeDeposit }) => {
          const d = (deposits || [])[0];
          if (!d || !onChangeDeposit) return;
          if (d.id) {
            onChangeDeposit({ _destroy: true });
          } else {
            onChangeDeposit({ value: null, relative_due_date: null, specific_due_date: null });
          }
        },
        renderBody: ({ deposits, onChangeDeposit }) => {
          const d = (deposits || [])[0] || {};
          const depositType = d.deposit_type || 'flat';
          const dueDateType = d.due_date_type || 'relative';
          const set = (patch) => onChangeDeposit && onChangeDeposit(patch);
          return (
            <div className="deal-terms-deposit-grid">
              {/* Type segmented (% / $) */}
              <label className="deal-terms-field">
                <span className="deal-terms-field__label">Type</span>
                <SegmentedControl
                  value={depositType}
                  onChange={(v) => set({ deposit_type: v })}
                  options={[
                    { value: 'percent', label: '%' },
                    { value: 'flat', label: '$' },
                  ]}
                />
              </label>

              {/* Amount */}
              <label className="deal-terms-field deal-terms-deposit-grid__amount">
                <span className="deal-terms-field__label">Amount</span>
                <NumberInput
                  prefix={depositType === 'percent' ? null : '$'}
                  suffix={depositType === 'percent' ? '%' : 'USD'}
                  value={d.value}
                  onChange={(v) => set({ value: v })}
                />
              </label>

              {/* Due segmented (Relative / Specific) */}
              <label className="deal-terms-field">
                <span className="deal-terms-field__label">Due</span>
                <SegmentedControl
                  value={dueDateType}
                  onChange={(v) => set({ due_date_type: v })}
                  options={[
                    { value: 'relative', label: 'Relative' },
                    { value: 'specific', label: 'Specific' },
                  ]}
                />
              </label>

              {/* Days before event (Relative) or Date (Specific) */}
              {dueDateType === 'relative' ? (
                <label className="deal-terms-field">
                  <span className="deal-terms-field__label">Days before event</span>
                  <NumberInput
                    step="1"
                    value={d.relative_due_date}
                    onChange={(v) => set({ relative_due_date: v })}
                  />
                </label>
              ) : (
                <label className="deal-terms-field">
                  <span className="deal-terms-field__label">Date</span>
                  <span className="deal-terms-field__input-wrap">
                    <input
                      type="date"
                      className="deal-terms-field__input"
                      value={d.specific_due_date || ''}
                      onChange={(e) => set({ specific_due_date: e.target.value || null })}
                    />
                  </span>
                </label>
              )}
            </div>
          );
        },
      },
    ],
  },

  {
    key: 'bonuses',
    title: 'Bonuses',
    icon: 'fal fa-gift',
    items: [
      {
        key: 'bonus_tiers',
        title: 'Bonus tiers',
        description: 'Reward overages with extra payouts at sales tiers.',
        icon: 'fal fa-trophy',
        isConfigured: ({ bonusManagerProps }) => (
          ((bonusManagerProps?.budget?.budget_bonuses) || [])
            .filter((b) => !b._destroy).length > 0
        ),
        summaryChips: ({ bonusManagerProps }) => {
          const count = ((bonusManagerProps?.budget?.budget_bonuses) || [])
            .filter((b) => !b._destroy).length;
          return count > 0 ? [{ label: '', value: `${count} ${count === 1 ? 'tier' : 'tiers'}` }] : [];
        },
        clear: ({ bonusManagerProps }) => {
          // Delete each bonus through the persisting handler: saved rows DELETE
          // on the server, unsaved rows drop locally. Keyed by id, so the
          // per-row calls don't race.
          const current = bonusManagerProps?.budget?.budget_bonuses || [];
          if (!bonusManagerProps?.deleteBudgetBonus) return;
          current.forEach((b) => {
            bonusManagerProps.deleteBudgetBonus(
              bonusManagerProps.csrfToken,
              bonusManagerProps.team,
              b
            );
          });
        },
        renderBody: ({ bonusManagerProps, BonusManager }) => (
          bonusManagerProps && BonusManager
            ? <BonusManager {...bonusManagerProps} />
            : <div className="deal-terms-empty">Bonus manager unavailable in this context.</div>
        ),
      },
    ],
  },

  {
    key: 'merch_rates',
    title: 'Merch',
    icon: 'fal fa-tshirt',
    items: [
      {
        key: 'merch',
        title: 'Merch',
        description: 'Merch rates, sellers, and notes.',
        icon: 'fal fa-tshirt',
        isConfigured: ({ offer }) => (
          offer.soft_merch_rate != null
          || offer.hard_merch_rate != null
          || !!offer.soft_merch_sold_by
          || !!offer.hard_merch_sold_by
          || !!(offer.soft_merch_notes && String(offer.soft_merch_notes).trim())
          || !!(offer.hard_merch_notes && String(offer.hard_merch_notes).trim())
          || !!(offer.addl_merch_notes && String(offer.addl_merch_notes).trim())
        ),
        summaryChips: ({ offer }) => {
          const chips = [];
          if (offer.soft_merch_rate != null) chips.push({ label: 'Soft', value: fmtPct(offer.soft_merch_rate) });
          if (offer.hard_merch_rate != null) chips.push({ label: 'Hard', value: fmtPct(offer.hard_merch_rate) });
          if (offer.soft_merch_sold_by) chips.push({ label: 'Soft seller', value: formatSoldBy(offer.soft_merch_sold_by) });
          if (offer.hard_merch_sold_by) chips.push({ label: 'Hard seller', value: formatSoldBy(offer.hard_merch_sold_by) });
          return chips;
        },
        clear: ({ onChangeField }) => {
          onChangeField('soft_merch_rate', null);
          onChangeField('hard_merch_rate', null);
          onChangeField('soft_merch_sold_by', '');
          onChangeField('hard_merch_sold_by', '');
          onChangeField('soft_merch_notes', '');
          onChangeField('hard_merch_notes', '');
          onChangeField('addl_merch_notes', '');
        },
        renderBody: ({ offer, onChangeField }) => {
          const soldByOptions = [
            { value: '', label: 'Select…' },
            { value: 'artist', label: 'Artist' },
            { value: 'venue', label: 'Venue' },
            { value: '3rd_party', label: '3rd-party' },
          ];
          const renderSoldBy = (key) => (
            <select
              className="deal-terms-field__select deal-terms-field__select--full"
              value={offer[key] || ''}
              onChange={(e) => onChangeField(key, e.target.value)}
            >
              {soldByOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          );
          const renderText = (key) => (
            <input
              type="text"
              className="deal-terms-field__input deal-terms-field__input--bordered"
              value={offer[key] || ''}
              onChange={(e) => onChangeField(key, e.target.value)}
            />
          );
          return (
            <div className="deal-terms-merch-grid">
              {/* Soft row */}
              <Field label="Soft Merch Rate (artist %)">
                <NumberInput
                  suffix="%"
                  value={offer.soft_merch_rate}
                  onChange={(v) => onChangeField('soft_merch_rate', v)}
                />
              </Field>
              <Field label="Sold By">{renderSoldBy('soft_merch_sold_by')}</Field>
              <Field label="Soft Merch Notes">{renderText('soft_merch_notes')}</Field>

              {/* Hard row */}
              <Field label="Hard Merch Rate (artist %)">
                <NumberInput
                  suffix="%"
                  value={offer.hard_merch_rate}
                  onChange={(v) => onChangeField('hard_merch_rate', v)}
                />
              </Field>
              <Field label="Sold By">{renderSoldBy('hard_merch_sold_by')}</Field>
              <Field label="Hard Merch Notes">{renderText('hard_merch_notes')}</Field>

              {/* Full-width additional notes */}
              <label className="deal-terms-field deal-terms-merch-grid__full">
                <span className="deal-terms-field__label">Additional Merch Notes</span>
                <input
                  type="text"
                  className="deal-terms-field__input deal-terms-field__input--bordered"
                  value={offer.addl_merch_notes || ''}
                  onChange={(e) => onChangeField('addl_merch_notes', e.target.value)}
                />
              </label>
            </div>
          );
        },
      },
    ],
  },

  {
    key: 'taxes',
    title: 'Taxes',
    icon: 'fal fa-receipt',
    items: [
      {
        key: 'tax_withholding',
        title: 'Tax withholding',
        description: "Withhold a percentage of the artist's taxable earnings.",
        icon: 'fal fa-percentage',
        isConfigured: ({ offer }) => !!offer.has_tax_withholding,
        summaryChips: ({ offer }) => {
          if (!offer.has_tax_withholding) return [];
          return offer.tax_withholding_percentage != null
            ? [{ label: '', value: fmtPct(offer.tax_withholding_percentage) }]
            : [{ label: '', value: 'On' }];
        },
        clear: ({ onChangeField }) => {
          onChangeField('has_tax_withholding', false);
          onChangeField('tax_withholding_percentage', null);
        },
        renderBody: ({ offer, onChangeField }) => (
          <div className="deal-terms-grid">
            <Toggle
              checked={offer.has_tax_withholding}
              onChange={(v) => onChangeField('has_tax_withholding', v)}
              label="Withhold a % of taxable earnings"
            />
            {offer.has_tax_withholding && (
              <Field label="Withholding %">
                <NumberInput
                  suffix="%"
                  value={offer.tax_withholding_percentage}
                  onChange={(v) => onChangeField('tax_withholding_percentage', v)}
                />
              </Field>
            )}
          </div>
        ),
      },
      {
        key: 'tax_threshold',
        title: 'Minimum threshold',
        description: 'No withholding below this dollar amount.',
        icon: 'fal fa-filter',
        isConfigured: ({ offer }) => offer.tax_withholding_minimum_threshold != null,
        summaryChips: ({ offer }) => (
          offer.tax_withholding_minimum_threshold != null
            ? [{ label: '', value: fmtUSD(offer.tax_withholding_minimum_threshold) }]
            : []
        ),
        clear: ({ onChangeField }) => onChangeField('tax_withholding_minimum_threshold', null),
        renderBody: ({ offer, onChangeField }) => (
          <Field label="Minimum threshold">
            <NumberInput
              prefix="$"
              value={offer.tax_withholding_minimum_threshold}
              onChange={(v) => onChangeField('tax_withholding_minimum_threshold', v)}
            />
          </Field>
        ),
      },
      {
        key: 'tax_due_days',
        title: 'Due in',
        description: 'How many days after the event withholding is due.',
        icon: 'fal fa-calendar-day',
        isConfigured: ({ offer }) => offer.tax_withholding_due_days != null,
        summaryChips: ({ offer }) => (
          offer.tax_withholding_due_days != null
            ? [{ label: '', value: `${offer.tax_withholding_due_days} days` }]
            : []
        ),
        clear: ({ onChangeField }) => onChangeField('tax_withholding_due_days', null),
        renderBody: ({ offer, onChangeField }) => (
          <Field label="Due in (days)">
            <NumberInput
              step="1"
              suffix="days"
              value={offer.tax_withholding_due_days}
              onChange={(v) => onChangeField('tax_withholding_due_days', v)}
            />
          </Field>
        ),
      },
      // Kept deliberately, alongside the % marker on the line item (O-4778).
      // The two cover different venues and neither replaces the other:
      //
      //   tax is a budget line item  -> mark it with %, which also flips this on
      //   tax is a per-ticket ManualTax -> there IS no line item to mark. The
      //     only tax row is the derived manual_tax_forecast_row, which carries
      //     kind manual_tax_projection (a SYSTEM_KIND, so no % button) and does
      //     not even appear until this toggle is already on. This is the only
      //     way those venues can reach the feature.
      {
        key: 'admission_tax',
        title: 'Tax after facility fee',
        description: 'Apply this offer’s tax after the facility fee (on the net gate) and add it on top as pass-through, so it does not reduce the deal. Uses the tax you already have — either a line item marked with %, or the venue’s per-ticket tax.',
        icon: 'fal fa-percentage',
        isConfigured: ({ offer }) => !!offer.apply_tax_after_facility_fee,
        summaryChips: ({ offer }) => (offer.apply_tax_after_facility_fee ? [{ label: '', value: 'On' }] : []),
        clear: ({ onChangeField }) => onChangeField('apply_tax_after_facility_fee', false),
        renderBody: ({ offer, onChangeField }) => (
          <div className="deal-terms-grid">
            <Toggle
              checked={offer.apply_tax_after_facility_fee}
              onChange={(v) => onChangeField('apply_tax_after_facility_fee', v)}
              label="Apply tax after the facility fee (pass-through)"
            />
          </div>
        ),
      },
    ],
  },

  {
    key: 'ancillary',
    title: 'Ancillary',
    icon: 'fal fa-truck',
    items: [
      {
        key: 'facility_fee',
        title: 'Facility fee',
        description: 'Per-event facility maintenance fee.',
        icon: 'fal fa-building',
        isConfigured: ({ offer }) => !!offer.has_facility_maintenance_fee,
        summaryChips: ({ offer }) => {
          if (!offer.has_facility_maintenance_fee) return [];
          return offer.facility_maintenance_fee != null
            ? [{ label: '', value: fmtUSD(offer.facility_maintenance_fee) }]
            : [{ label: '', value: 'On' }];
        },
        clear: ({ onChangeField }) => {
          onChangeField('has_facility_maintenance_fee', false);
          onChangeField('facility_maintenance_fee', null);
        },
        renderBody: ({ offer, onChangeField }) => (
          <div className="deal-terms-grid">
            <Toggle
              checked={offer.has_facility_maintenance_fee}
              onChange={(v) => onChangeField('has_facility_maintenance_fee', v)}
              label="Charge a facility fee"
            />
            {offer.has_facility_maintenance_fee && (
              <Field label="Facility fee $">
                <NumberInput
                  prefix="$"
                  value={offer.facility_maintenance_fee}
                  onChange={(v) => onChangeField('facility_maintenance_fee', v)}
                />
              </Field>
            )}
          </div>
        ),
      },
      {
        key: 'charity_deduction',
        title: 'Charity deduction',
        description: 'Per-event charity deduction.',
        icon: 'fal fa-hand-holding-heart',
        isConfigured: ({ offer }) => !!offer.has_charity_deduction,
        summaryChips: ({ offer }) => {
          if (!offer.has_charity_deduction) return [];
          return offer.charity_deduction != null
            ? [{ label: '', value: fmtUSD(offer.charity_deduction) }]
            : [{ label: '', value: 'On' }];
        },
        clear: ({ onChangeField }) => {
          onChangeField('has_charity_deduction', false);
          onChangeField('charity_deduction', null);
        },
        renderBody: ({ offer, onChangeField }) => (
          <div className="deal-terms-grid">
            <Toggle
              checked={offer.has_charity_deduction}
              onChange={(v) => onChangeField('has_charity_deduction', v)}
              label="Apply a charity deduction"
            />
            {offer.has_charity_deduction && (
              <Field label="Charity deduction $">
                <NumberInput
                  prefix="$"
                  value={offer.charity_deduction}
                  onChange={(v) => onChangeField('charity_deduction', v)}
                />
              </Field>
            )}
          </div>
        ),
      },
      {
        key: 'promoter_profit',
        title: 'Promoter profit',
        description: 'Keep a percentage of expenses plus the guarantee.',
        icon: 'fal fa-hand-holding-usd',
        isConfigured: ({ offer }) => !!offer.promoter_profit,
        summaryChips: ({ offer }) => {
          if (!offer.promoter_profit) return [];
          return offer.promoter_profit_percentage != null
            ? [{ label: '', value: fmtPct(offer.promoter_profit_percentage) }]
            : [{ label: '', value: 'On' }];
        },
        clear: ({ onChangeField }) => {
          onChangeField('promoter_profit', false);
          onChangeField('promoter_profit_percentage', 0);
        },
        renderBody: ({ offer, onChangeField }) => (
          <div className="deal-terms-stack">
            <SetlistToggle
              label="Take a promoter profit"
              checked={!!offer.promoter_profit}
              onChange={(e) => onChangeField('promoter_profit', e.target.checked)}
            />
            {offer.promoter_profit && (
              <div className="deal-terms-stack__field">
                <TextInput
                  size="sm"
                  label="Percentage"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  trailingText="%"
                  hint="Of expenses plus the guarantee."
                  value={offer.promoter_profit_percentage ?? ''}
                  onChange={(e) => onChangeField(
                    'promoter_profit_percentage',
                    e.target.value === '' ? null : Number(e.target.value),
                  )}
                />
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'door_deal',
        title: 'Door deal payouts',
        description: 'Cap and floor for the artist payout.',
        icon: 'fal fa-door-open',
        isConfigured: ({ offer }) => (
          hasPayoutCap(offer.artist_payout_cap) || offer.artist_payout_floor != null
        ),
        summaryChips: ({ offer }) => {
          const chips = [];
          if (hasPayoutCap(offer.artist_payout_cap)) chips.push({ label: 'Cap', value: fmtUSD(offer.artist_payout_cap) });
          if (offer.artist_payout_floor != null) chips.push({ label: 'Floor', value: fmtUSD(offer.artist_payout_floor) });
          return chips;
        },
        clear: ({ onChangeField }) => {
          onChangeField('artist_payout_cap', null);
          onChangeField('artist_payout_floor', null);
        },
        renderBody: ({ offer, onChangeField }) => (
          <div className="deal-terms-grid">
            <Field label="Payout cap">
              <NumberInput
                prefix="$"
                value={offer.artist_payout_cap}
                onChange={(v) => onChangeField('artist_payout_cap', v)}
              />
            </Field>
            <Field label="Payout floor">
              <NumberInput
                prefix="$"
                value={offer.artist_payout_floor}
                onChange={(v) => onChangeField('artist_payout_floor', v)}
              />
            </Field>
          </div>
        ),
      },
    ],
  },

  {
    key: 'notes',
    title: 'Notes',
    icon: 'fal fa-comment-alt-lines',
    items: [
      // "Additional terms" — agent-facing free-form text. Maps to V3Offer.additional_terms
      // (the only existing offer-level long-text field besides guarantee_notes).
      // RichEditor stays per explicit user instruction.
      {
        key: 'offer_notes',
        title: 'Additional terms',
        description: 'Extra terms printed on the offer.',
        icon: 'fal fa-file-alt',
        isConfigured: ({ offer }) => !!(offer.additional_terms && String(offer.additional_terms).trim().length > 0),
        summaryChips: ({ offer }) => (
          offer.additional_terms && String(offer.additional_terms).trim().length > 0
            ? [{ label: '', value: 'Set' }]
            : []
        ),
        clear: ({ AdditionalTermsClear }) => AdditionalTermsClear && AdditionalTermsClear(),
        renderBody: ({ AdditionalTermsEditor }) => (
          AdditionalTermsEditor ? <AdditionalTermsEditor /> : null
        ),
      },
      {
        key: 'guarantee_notes',
        title: 'Guarantee notes',
        description: 'Conditions or carve-outs in the guarantee.',
        icon: 'fal fa-sticky-note',
        isConfigured: ({ offer }) => !!(offer.guarantee_notes && String(offer.guarantee_notes).trim().length > 0),
        summaryChips: ({ offer }) => (
          offer.guarantee_notes && String(offer.guarantee_notes).trim().length > 0
            ? [{ label: '', value: 'Set' }]
            : []
        ),
        clear: ({ onChangeField }) => onChangeField('guarantee_notes', ''),
        renderBody: ({ GuaranteeNotesEditor }) => (
          GuaranteeNotesEditor ? <GuaranteeNotesEditor /> : null
        ),
      },
      // "Internal notes" from the screenshot has no backing field on V3Offer
      // (no internal_notes column). Per the user's "do not create anything new"
      // rule, the item is intentionally omitted until the field exists.
    ],
  },
];

// Flat helpers used by the card-level header chip.
export const countConfigured = (sections, ctx) => {
  let total = 0;
  let configured = 0;
  sections.forEach((s) => {
    s.items.forEach((it) => {
      total += 1;
      if (it.isConfigured(ctx)) configured += 1;
    });
  });
  return { total, configured };
};
